/**
 * WebBridge — the plain-browser surface (DESIGN-client §13). Identity is a server guest
 * session (`GET /api/auth/session`, token kept in localStorage `sn.token`; see identity.ts).
 * No voice, no invite dialog, no Discord layout modes; orientation from the viewport.
 */
import {
  type ActivityBridge,
  type ActivityParticipant,
  type ActivityPlatform,
  type LayoutMode,
  type Orientation,
  type VoiceUpdate,
  detectPlatform,
  vibrate,
  watchOrientation,
} from './ActivityBridge';

const TAB_ID = `web-${Math.random().toString(36).slice(2, 10)}`;

export class WebBridge implements ActivityBridge {
  readonly kind = 'web' as const;
  private readonly _platform: ActivityPlatform;

  constructor(platform: ActivityPlatform = detectPlatform()) {
    this._platform = platform;
  }

  async ready(): Promise<void> {}

  async authorize(): Promise<{ code: string }> {
    return { code: 'web-guest' };
  }

  async authenticate(): Promise<void> {}

  instanceId(): string {
    return TAB_ID;
  }

  channelId(): string | null {
    return null;
  }

  guildId(): string | null {
    return null;
  }

  platform(): ActivityPlatform {
    return this._platform;
  }

  subscribeParticipants(cb: (participants: ActivityParticipant[]) => void): () => void {
    const t = setTimeout(() => cb([]), 0);
    return () => clearTimeout(t);
  }

  voiceAvailable(): boolean {
    return false;
  }

  subscribeVoice(_cb: (u: VoiceUpdate) => void): () => void {
    return () => {};
  }

  inviteAvailable(): boolean {
    return false;
  }

  async openInvite(): Promise<void> {
    throw new Error('web: no invite dialog; share the table code');
  }

  async openExternalLink(url: string): Promise<void> {
    globalThis.window?.open(url, '_blank', 'noopener');
  }

  subscribeLayoutMode(cb: (m: LayoutMode) => void): () => void {
    cb('focused');
    return () => {};
  }

  subscribeOrientation(cb: (o: Orientation) => void): () => void {
    return watchOrientation(cb);
  }

  /** Screen Orientation API where available (Android Chrome in fullscreen); fail-soft. */
  async lockOrientation(): Promise<void> {
    if (this._platform !== 'mobile') return;
    try {
      const so = globalThis.screen?.orientation as { lock?: (o: string) => Promise<void> } | undefined;
      await so?.lock?.('portrait');
    } catch {
      /* not allowed outside fullscreen / unsupported */
    }
  }

  async encourageHardwareAcceleration(): Promise<void> {}

  haptic(kind: 'light' | 'medium'): void {
    vibrate(kind);
  }

  mockUser(): { id: string; username: string } | null {
    return null;
  }
}
